/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useState,
  useEffect,
} from 'react';
import { useQuery } from '@apollo/client';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import hoistStatics from 'hoist-non-react-statics';
import PropTypes from 'prop-types';
import {
  Dropdown,
  Icon,
} from 'semantic-ui-react';
import { IfScope, AUTHENTICATION_SCOPES } from '@playbookux/playbook-web-core';

/* eslint-disable import/no-unresolved */
import useStore from 'playbook-web/store';

import { isEmpty } from 'lodash';
import { config } from '../../../config';
import Avatar from '../avatar';
import Tabs from '../../../company/constants/tabs';
import TesterTabs from '../../../tester/constants/tabs';
import { GET_USER_BY_ID } from '../../graphql/user';

import {
  Container,
  CustomDropdown,
  Grow,
  Link,
  Logo,
  Menu,
  MenuItem,
} from './styles';
import WorkspaceDropdown from './workspace-dropdown/index';

import { platformType } from '../../utils/platform-type';
import {
  signOut,
  setCurrentCompany,
  fetchCompany,
  updateUserStatus,
  setUser,
} from '../../actions';
import { getIsCompanyMemberByCompanyId } from '../../services/graphql-requests';
import CompanyChannel from '../../../cable/channels/company-channel';
import AdminChannel from '../../../cable/channels/admin-channel';
import TesterChannel from '../../../cable/channels/tester-channel';
import { registerFunctions } from '../../hooks/use-function-handler';
import TesterAllChannel from '../../../cable/channels/tester-all-channel';

export default (options = {}) => (WrappedComponent) => {
  const WithLayout = (props) => {
    /** props */
    const {
      me,
      company,
      history: { push },
      onSetCurrentCompany,
      onFetchCompany,
      companyId,
      onSignOut,
      scope,
      status,
      onUpdateUserStatus,
      onSetUser,
      location: { pathname },
    } = props;

    const {
      showLogo = true,
      selectedTab,
      hideCompanyDropdown = false,
      onLogoClick = null,
      blocked,
      rejected,
    } = options;

    const setCompany = useStore((state) => state.setCompany);
    const setUserWithProfile = useStore((state) => state.setUser);
    const setLogout = useStore((state) => state.setLogout);

    /** states */
    const [executingOnLogoClick, setExecutingOnLogoClick] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    /** graphql */
    const userId = (me && me.user_id) || undefined;

    /**
     * An issue happens when this URL "share/highlight-reel/graphql(project or clip)" hits on User query
     *it means schema doesn't have a user method to hit. Then, we need to skip query when it's share scope.
     *User is also logged out after going back to "/" route. Why? I don't know.
     */
    const shareSkip = scope === AUTHENTICATION_SCOPES.SHARE_PROJECT
      || scope === AUTHENTICATION_SCOPES.SHARE_CLIP
      || scope === AUTHENTICATION_SCOPES.SHARE_HIGHLIGHT_REEL;

    const {
      data: { user = {} } = {},
    } = useQuery(GET_USER_BY_ID, {
      variables: { userId },
      skip: !userId || shareSkip,
    });

    // user that is a tester and guest at same time, please leave.
    if (config.REACT_APP_PLATFORM_TYPE === 'tester' && user?.guest && user?.tester) {
      onSignOut();
    }

    // logout tester if token is null
    if (config.REACT_APP_PLATFORM_TYPE === 'tester' && !userId) {
      onSignOut();
    }

    useEffect(() => {
      // when setting user, it must contains profile otherwise the MF create project won't work correctly
      setUserWithProfile(user);
      onUpdateUserStatus(user.status);
      onSetUser(user);
    }, [user]);

    useEffect(() => {
      // add to mf store as OBJECT and not companyId
      setCompany(company);
    }, [company, setCompany]);

    registerFunctions('get_companies', {
      func1: onFetchCompany,
    });

    const fetchIsCompanyMember = async (_companyId) => {
      try {
        // logout user if is not company member
        const { isCompanyMember, error } = await getIsCompanyMemberByCompanyId(_companyId);
        if (!isCompanyMember || error) {
          if (pathname !== '/create-workspace' && _companyId === 'addWorkspace') {
            onSignOut();
          }
        }
      } catch {
        onSignOut();
      }
    };

    useEffect(() => {
      const resize = () => {
        setIsMobile(window.innerWidth < 768);
      };
      window.addEventListener('resize', resize);
      if (setLogout) {
        setLogout(() => onSignOut());
      }

      if (scope === AUTHENTICATION_SCOPES.USER && config.REACT_APP_PLATFORM_TYPE === 'company') {
        if (companyId) {
          fetchIsCompanyMember(companyId);
        }
      }

      return () => {
        window.removeEventListener('resize', resize);
      };
    }, []);

    const handleSignOutClick = () => {
      onSignOut();
      push('/');
    };

    const handleAccountClick = () => {
      push('/account');
    };

    const handleManageTagsClick = () => {
      push('/manage-tags');
    };

    const handleManageFoldersClick = () => {
      push('/manage-folders');
    };

    const handleTemplateLibraryClick = () => {
      push('/template-library');
    };

    const handleIntegrationsClick = () => {
      push('/integrations');
    };

    const handleHelperCenterClick = () => {
      window.open('https://help.playbookux.com/');
    };

    const handleWhatsNew = () => {
      window.open('https://www.notion.so/What-s-New-1f7eb3309fdf4724948804432c2f8426', '_blank');
    };

    const handleCompanyChange = async (companyIdSelected) => {
      if (companyId === companyIdSelected) return;

      if (companyIdSelected === 'addWorkspace') {
        push('/create-workspace');
        return;
      }
      const companyData = await onFetchCompany(companyIdSelected);
      onSetCurrentCompany(companyData);
      push('/');
    };

    const handleLogoClick = async () => {
      if (onLogoClick && !executingOnLogoClick) {
        setExecutingOnLogoClick(true);
        await onLogoClick(props);
        setExecutingOnLogoClick(false);
        return;
      }

      push('/');
    };

    return (
      <>
        <Menu pointing secondary borderless fixed="top" fluid size="massive">
          <Container>
            <Grow>
              <Link
                showLogo={showLogo}
                onClick={handleLogoClick}
              >
                <Logo src="/images/playbook-logo.png" alt="Playbook" />
              </Link>
              <IfScope user scope={scope}>
                {platformType === 'company' && company && !hideCompanyDropdown && (
                  <>
                    <WorkspaceDropdown
                      handleCompanyChange={handleCompanyChange}
                    />
                  </>
                )}
              </IfScope>
            </Grow>

            {
              platformType === 'admin' && (<AdminChannel />)
            }
            {
              platformType === 'tester' && (
                <>
                  <TesterChannel />
                  <TesterAllChannel />
                </>
              )
            }
            {
              platformType === 'company' && (<CompanyChannel />)
            }

            <IfScope tester scope={scope}>
              {status === 'active' && isMobile === false && (!rejected && !blocked) && (
                <>
                  <MenuItem
                    name="Browse Tests"
                    active={TesterTabs.BROWSE_TESTS === selectedTab}
                    onClick={() => push('/')}
                  />
                  <MenuItem
                    name="Completed Tests"
                    active={TesterTabs.COMPLETED_TESTS === selectedTab}
                    onClick={() => push('/completed-tests')}
                  />
                  <MenuItem
                    name="FAQ"
                    active={TesterTabs.FAQS === selectedTab}
                    onClick={() => push('/faqs')}
                  />
                  <MenuItem
                    name="My Profile"
                    active={TesterTabs.MY_PROFILE === selectedTab}
                    onClick={() => push('/profile')}
                  />

                </>
              )}
              <CustomDropdown
                item
                trigger={isMobile ? (
                  <Icon
                    name="bars"
                    size="large"
                  />
                ) : (
                  <Avatar
                    height="32px"
                    width="32px"
                    color="#CFDFFC"
                    firstName={(me && me.first_name) || ''}
                  />
                )}
              >
                <Dropdown.Menu direction="left">
                  {user.status === 'active' && isMobile === true && (
                    <>
                      <Dropdown.Item
                        active={TesterTabs.BROWSE_TESTS === selectedTab}
                        onClick={() => push('/')}
                      >
                        Browse Tests
                      </Dropdown.Item>
                      <Dropdown.Item
                        active={TesterTabs.COMPLETED_TESTS === selectedTab}
                        onClick={() => push('/completed-tests')}
                      >
                        Completed Tests
                      </Dropdown.Item>
                      <Dropdown.Item
                        active={TesterTabs.FAQS === selectedTab}
                        onClick={() => push('/faqs')}
                      >
                        FAQ
                      </Dropdown.Item>
                      <Dropdown.Item
                        active={TesterTabs.MY_PROFILE === selectedTab}
                        onClick={() => push('/profile')}
                      >
                        My Profile
                      </Dropdown.Item>
                    </>
                  )}
                  <Dropdown.Item
                    onClick={() => push('/settings')}
                    active={TesterTabs.SETTINGS === selectedTab}
                  >
                    Settings
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleSignOutClick}>
                    Sign Out
                  </Dropdown.Item>
                </Dropdown.Menu>
              </CustomDropdown>
            </IfScope>

            <IfScope user scope={scope}>
              {platformType !== 'admin' && company && (
                <>
                  <MenuItem
                    name="Projects"
                    active={Tabs.PROJECT === selectedTab}
                    onClick={() => push('/')}
                  />
                  <MenuItem
                    name="Highlight Reel"
                    active={Tabs.HIGHLIGHT_REEL === selectedTab}
                    onClick={() => push('/highlight-reel')}
                  />
                  <MenuItem
                    name="Tags"
                    active={Tabs.TAGS === selectedTab}
                    onClick={() => push('/tags')}
                  />
                  <MenuItem
                    name="Search"
                    active={Tabs.SEARCH === selectedTab}
                    onClick={() => push('/search')}
                  />
                </>
              )}
              <CustomDropdown
                item
                data-cy="nav-dropdown"
                trigger={(
                  <Avatar
                    height="32px"
                    width="32px"
                    color="#CFDFFC"
                    firstName={(me && me.first_name) || ''}
                  />
                )}
              >
                <Dropdown.Menu direction="left">
                  {platformType === 'company' && (
                    <>
                      {!isEmpty(company) && (
                        <>
                          <Dropdown.Item onClick={handleAccountClick} data-profile="account">
                            Account
                          </Dropdown.Item>
                          <Dropdown.Item onClick={handleManageTagsClick} data-profile="manage-tags">
                            Manage Tags
                          </Dropdown.Item>
                          <Dropdown.Item onClick={handleManageFoldersClick} data-profile="manage-folders">
                            Manage Folders
                          </Dropdown.Item>
                          <Dropdown.Item onClick={handleTemplateLibraryClick} data-profile="template-library">
                            Template Library
                          </Dropdown.Item>

                        </>
                      )}
                      <Dropdown.Item onClick={handleIntegrationsClick}>
                        Integrations
                      </Dropdown.Item>
                      <Dropdown.Item onClick={handleHelperCenterClick}>
                        Help Center
                      </Dropdown.Item>
                      <Dropdown.Item onClick={handleWhatsNew}>
                        What’s New
                      </Dropdown.Item>
                    </>
                  )}
                  <Dropdown.Item onClick={handleSignOutClick}>
                    Sign Out
                  </Dropdown.Item>
                </Dropdown.Menu>
              </CustomDropdown>
            </IfScope>
          </Container>
        </Menu>

        <WrappedComponent {...props} />
      </>
    );
  };

  WithLayout.propTypes = {
    history: PropTypes.shape({
      push: PropTypes.func,
    }).isRequired,
    me: PropTypes.shape({
      first_name: PropTypes.string,
      user_id: PropTypes.string,
    }).isRequired,
    scope: PropTypes.string.isRequired,
    company: PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string,
    }),
    onSignOut: PropTypes.func.isRequired,
    companyId: PropTypes.string,
    onFetchCompany: PropTypes.func.isRequired,
    onSetCurrentCompany: PropTypes.func.isRequired,
    hideCompanyDropdown: PropTypes.bool,
    status: PropTypes.string,
    onUpdateUserStatus: PropTypes.func.isRequired,
    onSetUser: PropTypes.func.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }).isRequired,
    token: PropTypes.string.isRequired,
  };

  WithLayout.defaultProps = {
    hideCompanyDropdown: false,
    companyId: '',
    status: '',
    company: {},
  };

  const mapStateToProps = ({ authentication }) => ({
    me: authentication.profile,
    token: authentication.token,
    company: authentication.company,
    companyId: authentication.company?.id,
    scope: authentication && authentication.scope,
    status: authentication.status,
  });

  const mapDispatchToProps = (dispatch) => bindActionCreators({
    onSetCurrentCompany: setCurrentCompany,
    onFetchCompany: fetchCompany,
    onSignOut: signOut,
    onUpdateUserStatus: updateUserStatus,
    onSetUser: setUser,
  }, dispatch);

  return compose(
    connect(mapStateToProps, mapDispatchToProps),
  )(hoistStatics(WithLayout, WrappedComponent));
};
